<template>
  <main class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased" :style="{
    '--pointer-x': `${pointer.x}%`,
    '--pointer-y': `${pointer.y}%`
  }" @pointermove="handlePointerMove">
    <!-- AMBIENTE -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <!-- VOLTAR AO TOPO -->
    <transition name="scroll-top">
      <button v-show="showScrollTop" type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo" title="Voltar ao topo" @click="scrollToTop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          class="h-5 w-5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div
            class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-2xl">
            <span class="relative flex h-2 w-2">
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-40"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            Soluções
            <span class="hidden text-[var(--muted-2)] sm:inline">/ Build what matters</span>
          </div>

          <h1
            class="mx-auto mt-7 max-w-5xl text-5xl font-semibold tracking-[-0.065em] text-[var(--text-strong)] sm:text-6xl lg:text-[5.45rem] lg:leading-[0.98]">
            Escolha o caminho.
            <span class="hero-gradient block">Nós cuidamos da complexidade.</span>
          </h1>

          <p class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl">
            Serviços, módulos e trilhas para transformar ideias e operações em produtos digitais
            claros, rápidos e preparados para evoluir.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a :href="whatsHref" target="_blank" rel="noopener"
              class="primary-button group inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]">
              Solicitar proposta
              <span class="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true">→</span>
            </a>

            <router-link to="/projects"
              class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
              Ver projetos
            </router-link>
          </div>
        </div>

        <!-- DELIVERY SYSTEM -->
        <div class="relative mx-auto mt-16 max-w-6xl" data-reveal>
          <div class="hero-halo absolute inset-x-16 top-14 h-44 rounded-full blur-3xl"></div>

          <div class="device-frame relative rounded-[2.3rem] p-2.5 sm:rounded-[2.8rem] sm:p-3">
            <div
              class="overflow-hidden rounded-[1.8rem] border border-white/10 bg-[#08090b] text-white sm:rounded-[2.35rem]">
              <div class="flex items-center justify-between border-b border-white/[0.07] px-5 py-4 sm:px-6">
                <div class="flex gap-2">
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                </div>

                <div
                  class="hidden rounded-full bg-white/[0.06] px-4 py-2 text-[10px] font-semibold text-zinc-400 ring-1 ring-white/10 sm:block">
                  Grivaro / delivery-system
                </div>

                <span class="rounded-full bg-white/[0.07] px-3 py-1.5 text-[10px] font-semibold text-zinc-300">
                  Active
                </span>
              </div>

              <div class="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1fr_.75fr] lg:p-9">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                    Delivery System
                  </p>

                  <h2 class="mt-4 max-w-2xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
                    Do diagnóstico ao lançamento, sem excesso de complexidade.
                  </h2>

                  <p class="mt-5 max-w-xl text-sm leading-7 text-zinc-400">
                    Um fluxo pensado para definir o essencial, construir rápido e entregar algo
                    que já nasce preparado para operação real.
                  </p>

                  <div class="mt-8 grid gap-3 sm:grid-cols-3">
                    <article v-for="item in highlights" :key="item.title" class="dashboard-card rounded-[1.3rem] p-4">
                      <div class="mb-3 h-1 w-8 rounded-full bg-white/25"></div>
                      <p class="text-xs font-semibold text-white">{{ item.title }}</p>
                      <p class="mt-1.5 text-[11px] leading-5 text-zinc-500">{{ item.desc }}</p>
                    </article>
                  </div>
                </div>

                <div class="grid content-start gap-3 sm:grid-cols-3 lg:grid-cols-1">
                  <article v-for="metric in metrics" :key="metric.label"
                    class="dashboard-card rounded-[1.5rem] p-5 sm:p-6">
                    <p class="text-3xl font-semibold tracking-[-0.04em]">{{ metric.value }}</p>
                    <p class="mt-2 text-xs leading-5 text-zinc-500">{{ metric.label }}</p>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <!-- SEARCH DOCK -->
          <div
            class="search-dock relative z-20 mx-auto mt-5 w-[94%] rounded-[1.6rem] border border-[var(--border)] bg-[var(--glass-strong)] p-3 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:mt-6 sm:w-[90%]">
            <div class="grid gap-3 lg:grid-cols-[1fr_auto]">
              <label class="relative block">
                <span class="sr-only">Buscar solução</span>

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.8"
                  class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-2)]"
                  aria-hidden="true">
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m20 20-3.5-3.5"></path>
                </svg>

                <input v-model="q" type="search" placeholder="Buscar por solução, tecnologia ou problema"
                  class="h-12 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] pl-11 pr-5 text-sm font-medium text-[var(--text-strong)] outline-none transition placeholder:text-[var(--muted-2)] focus:border-[var(--text-strong)]" />
              </label>

              <div class="flex flex-wrap gap-2">
                <button v-for="category in categoriesUi" :key="category.value" type="button"
                  @click="setCategory(category.value)" :aria-pressed="activeCategory === category.value" :class="[
                    'h-12 rounded-full px-4 text-xs font-semibold transition duration-300 sm:px-5',
                    activeCategory === category.value
                      ? 'bg-[var(--text-strong)] text-[var(--inverse)]'
                      : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:-translate-y-0.5 hover:text-[var(--text-strong)]'
                  ]">
                  {{ category.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONNECTOR -->
    <div class="relative z-10 mx-auto mt-10 h-20 max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="flow-line"></div>
      <div class="flow-node"></div>
    </div>

    <!-- CATÁLOGO -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="eyebrow">Catálogo</p>
            <h2 class="section-title mt-4">
              Serviços essenciais.
              <span class="text-[var(--muted-2)]">Sem engessar o projeto.</span>
            </h2>
          </div>

          <div class="lg:col-span-5">
            <p class="text-lg leading-8 text-[var(--muted)]">
              Escolha uma frente específica ou combine módulos para montar uma solução que acompanhe
              o processo real da sua operação.
            </p>

            <p class="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted-2)]" aria-live="polite">
              {{ filteredServices.length }}
              {{ filteredServices.length === 1 ? 'solução encontrada' : 'soluções encontradas' }}
            </p>
          </div>
        </div>

        <transition-group v-if="filteredServices.length" name="catalog" tag="div"
          class="mt-14 grid gap-4 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
          <article v-for="(service, index) in filteredServices" :key="service.title" :class="[
            'service-card group relative overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--card)] p-7 transition duration-500',
            catalogSpan(index)
          ]">
            <div class="card-shine"></div>

            <div class="relative flex h-full flex-col">
              <div class="flex items-start justify-between gap-5">
                <div class="flex items-center gap-3">
                  <div
                    class="grid h-12 w-12 place-items-center rounded-2xl border border-[var(--border)] bg-[var(--surface)] text-xs font-bold text-[var(--text-strong)] shadow-sm">
                    {{ service.number }}
                  </div>

                  <span
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">
                    {{ service.badge }}
                  </span>
                </div>

                <span
                  class="text-2xl text-[var(--muted-2)] transition duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                  aria-hidden="true">
                  ↗
                </span>
              </div>

              <h3 class="mt-9 text-2xl font-semibold tracking-[-0.035em] text-[var(--text-strong)]">
                {{ service.title }}
              </h3>

              <p class="mt-3 max-w-xl text-sm leading-7 text-[var(--muted)]">
                {{ service.description }}
              </p>

              <div class="mt-7 flex flex-wrap gap-2">
                <span v-for="point in service.points" :key="point"
                  class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-medium text-[var(--muted)]">
                  {{ point }}
                </span>
              </div>

              <div class="mt-auto pt-8">
                <a :href="serviceWhatsHref(service)" target="_blank" rel="noopener"
                  class="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[var(--text-strong)] px-4 text-xs font-semibold text-[var(--inverse)] transition duration-300 hover:-translate-y-0.5">
                  Solicitar proposta
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </article>
        </transition-group>

        <div v-else
          class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center shadow-[var(--shadow-soft)] backdrop-blur-2xl">
          <p class="text-lg font-semibold text-[var(--text-strong)]">
            Nenhuma solução encontrada.
          </p>

          <p class="mt-2 text-sm text-[var(--muted)]">
            Tente outro termo ou volte para todas as categorias.
          </p>

          <button type="button" @click="clearFilters"
            class="secondary-button mt-6 inline-flex h-10 items-center justify-center rounded-full px-5 text-xs font-semibold">
            Limpar filtros
          </button>
        </div>
      </div>
    </section>

    <!-- CURVA -->
    <div class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="curve-transition">
        <span class="curve-dot"></span>
      </div>
    </div>

    <!-- TRILHAS -->
    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-3xl text-center" data-reveal>
          <p class="eyebrow">Trilhas</p>
          <h2 class="section-title mx-auto mt-4">
            Do zero ao valor em produção.
          </h2>

          <p class="mx-auto mt-5 max-w-2xl text-base leading-7 text-[var(--muted)]">
            Para quem prefere começar por um caminho já estruturado, sem perder a flexibilidade
            de adaptar escopo e prioridades.
          </p>
        </div>

        <div v-if="filteredTracks.length" class="mt-14 grid gap-5 lg:grid-cols-3 lg:items-start">
          <article v-for="(track, index) in filteredTracks" :key="track.title" :class="[
            'track-card relative overflow-hidden rounded-[2.2rem] border border-[var(--border)] bg-[var(--card)] p-7 shadow-[var(--shadow-soft)] backdrop-blur-xl transition duration-500',
            index === 1 ? 'lg:-translate-y-5' : ''
          ]" data-reveal>
            <div class="track-halo absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl" aria-hidden="true"></div>

            <div class="relative">
              <div class="flex items-center justify-between gap-4">
                <span
                  class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-2 text-xs font-semibold text-[var(--muted)]">
                  {{ track.badge }}
                </span>

                <span class="text-xs font-semibold text-[var(--muted-2)]">
                  {{ track.timeline }}
                </span>
              </div>

              <h3 class="mt-8 text-3xl font-semibold tracking-[-0.04em] text-[var(--text-strong)]">
                {{ track.title }}
              </h3>

              <p class="mt-4 text-sm leading-7 text-[var(--muted)]">
                {{ track.description }}
              </p>

              <div class="mt-7 grid gap-2">
                <div v-for="(deliverable, deliverableIndex) in track.deliverables" :key="deliverable"
                  class="flex items-center gap-3 rounded-[1.1rem] border border-[var(--border)] bg-[var(--surface)] px-4 py-3 text-xs font-semibold text-[var(--muted)]">
                  <span
                    class="grid h-6 w-6 place-items-center rounded-full bg-[var(--text-strong)] text-[9px] font-bold text-[var(--inverse)]">
                    {{ deliverableIndex + 1 }}
                  </span>
                  {{ deliverable }}
                </div>
              </div>

              <div class="mt-8 flex flex-col gap-2">
                <a :href="trackWhatsHref(track)" target="_blank" rel="noopener"
                  class="primary-button inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-xs font-semibold">
                  Começar trilha
                  <span aria-hidden="true">→</span>
                </a>

                <router-link to="/projects"
                  class="secondary-button inline-flex h-11 items-center justify-center rounded-full px-5 text-xs font-semibold">
                  Ver projetos
                </router-link>
              </div>
            </div>
          </article>
        </div>

        <div v-else
          class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center backdrop-blur-2xl">
          <p class="text-lg font-semibold text-[var(--text-strong)]">
            Nenhuma trilha encontrada.
          </p>
          <p class="mt-2 text-sm text-[var(--muted)]">
            Ajuste sua busca ou selecione outra categoria.
          </p>
        </div>
      </div>
    </section>

    <!-- PROCESSO -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          class="process-shell relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] bg-[var(--glass)] p-5 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-8 lg:p-10"
          data-reveal>
          <div class="process-glow" aria-hidden="true"></div>

          <div class="relative grid gap-12 lg:grid-cols-[.82fr_1.18fr]">
            <div class="lg:sticky lg:top-28">
              <p class="eyebrow">Processo</p>

              <h2 class="mt-4 text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Um método simples.
                <span class="block text-[var(--muted-2)]">Menos risco na execução.</span>
              </h2>

              <p class="mt-5 max-w-xl text-base leading-7 text-[var(--muted)]">
                Escopo, prioridade e decisão técnica ficam visíveis durante todo o projeto,
                sem transformar a execução em burocracia.
              </p>

              <div
                class="mt-8 inline-flex items-center gap-3 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-3 text-xs font-semibold text-[var(--muted)]">
                <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
                Ciclos curtos e entregas verificáveis
              </div>
            </div>

            <div class="relative">
              <div class="timeline-line absolute bottom-10 left-6 top-6 w-px" aria-hidden="true"></div>

              <div class="space-y-4">
                <article v-for="(step, index) in process" :key="step.title" class="group relative pl-16" data-reveal>
                  <div
                    class="absolute left-0 top-1 grid h-12 w-12 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-xs font-bold text-[var(--text-strong)] shadow-[var(--shadow-soft)] transition duration-500 group-hover:scale-110 group-hover:bg-[var(--text-strong)] group-hover:text-[var(--inverse)]">
                    {{ String(index + 1).padStart(2, '0') }}
                  </div>

                  <div
                    class="rounded-[1.6rem] border border-[var(--border)] bg-[var(--card)] p-5 transition duration-500 group-hover:translate-x-1 group-hover:shadow-[var(--shadow-soft)] sm:p-6">
                    <p class="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-2)]">
                      Etapa {{ index + 1 }}
                    </p>

                    <h3 class="mt-2 text-xl font-semibold tracking-tight text-[var(--text-strong)]">
                      {{ step.title }}
                    </h3>

                    <p class="mt-3 text-sm leading-6 text-[var(--muted)]">
                      {{ step.desc }}
                    </p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ DARK -->
    <div class="relative z-10 mx-auto -mb-8 mt-10 flex max-w-7xl justify-center" aria-hidden="true">
      <div
        class="rounded-full border border-[var(--border)] bg-[var(--glass-strong)] px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-xl">
        Escopo · Tecnologia · Prazo · Evolução
      </div>
    </div>

    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16">
          <div class="dark-panel-grid pointer-events-none absolute inset-0" aria-hidden="true"></div>
          <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-12 lg:grid-cols-12">
            <div class="lg:col-span-5" data-reveal>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Perguntas frequentes
              </p>

              <h2 class="mt-5 text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
                Antes de começar.
              </h2>

              <p class="mt-5 max-w-md text-base leading-7 text-zinc-400">
                As dúvidas mais comuns sobre escopo, prazo, tecnologia e continuidade do projeto.
              </p>

              <a :href="whatsHref" target="_blank" rel="noopener"
                class="mt-8 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-white px-5 text-xs font-semibold text-black transition hover:-translate-y-0.5 hover:bg-zinc-200">
                Tirar uma dúvida
                <span aria-hidden="true">→</span>
              </a>
            </div>

            <div class="lg:col-span-7">
              <div class="divide-y divide-white/10">
                <details v-for="faq in faqs" :key="faq.question" class="faq-item group py-5" data-reveal>
                  <summary
                    class="flex cursor-pointer list-none items-center justify-between gap-6 text-base font-semibold text-white">
                    <span>{{ faq.question }}</span>

                    <span
                      class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/[0.07] text-lg text-zinc-300 ring-1 ring-white/10 transition duration-300 group-open:rotate-45 group-open:bg-white group-open:text-black">
                      +
                    </span>
                  </summary>

                  <p class="mt-3 max-w-3xl pr-12 text-sm leading-7 text-zinc-400">
                    {{ faq.answer }}
                  </p>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="relative z-10 pb-24 pt-16 sm:pb-28 sm:pt-20">
      <div class="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div
          class="cta-panel relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] px-6 py-12 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:px-9 sm:py-14 lg:px-12"
          data-reveal>
          <div class="cta-orb absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-8">
              <p class="eyebrow">Próximo passo</p>

              <h2
                class="mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Vamos mapear a solução ideal para sua operação.
              </h2>

              <p class="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted)]">
                Conte seu contexto, objetivo e principais dores.
                A partir disso, organizamos uma proposta clara e viável.
              </p>
            </div>

            <div class="flex flex-col gap-3 lg:col-span-4">
              <a :href="whatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">
                Falar no WhatsApp
                <span aria-hidden="true">→</span>
              </a>

              <router-link to="/templates"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
                Ver templates
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from 'vue'

const whatsHref =
  'https://wa.me/77999631644?text=' +
  encodeURIComponent(
    'Olá! Quero conversar sobre uma solução digital sob medida com a Grivaro Studio.'
  )

const q = ref('')
const activeCategory = ref('all')
const showScrollTop = ref(false)
const pointer = reactive({ x: 50, y: 20 })

let observer = null

const categoriesUi = [
  { value: 'all', label: 'Todas' },
  { value: 'product', label: 'Produto' },
  { value: 'systems', label: 'Sistemas' },
  { value: 'integrations', label: 'Integrações' },
  { value: 'data', label: 'Dados' },
  { value: 'support', label: 'Suporte' }
]

const metrics = [
  { value: '4–8 sem.', label: 'média para um MVP bem definido' },
  { value: '100%', label: 'solução adaptada ao contexto' },
  { value: '1 plano', label: 'prioridade e escopo claros' }
]

const highlights = [
  {
    title: 'Escopo enxuto',
    desc: 'Priorizamos o que gera valor primeiro, sem inflar o projeto.'
  },
  {
    title: 'Design funcional',
    desc: 'Interfaces claras, consistentes e fáceis de usar no dia a dia.'
  },
  {
    title: 'Base escalável',
    desc: 'Arquitetura pensada para evoluir sem virar retrabalho.'
  }
]

const services = [
  {
    number: '01',
    category: 'product',
    badge: 'Produto',
    title: 'Desenvolvimento Web',
    description:
      'Sites, aplicações web e plataformas digitais com visual premium, performance e estrutura profissional.',
    points: [
      'Landing pages e sites institucionais',
      'Aplicações Vue/Nuxt',
      'SEO, responsividade e performance'
    ]
  },
  {
    number: '02',
    category: 'systems',
    badge: 'Operação',
    title: 'Sistemas Internos',
    description:
      'Backoffices, painéis e ferramentas para organizar processos e reduzir tarefas manuais.',
    points: [
      'Dashboards operacionais',
      'Permissões e usuários',
      'Relatórios e fluxos internos'
    ]
  },
  {
    number: '03',
    category: 'integrations',
    badge: 'Automação',
    title: 'Integrações',
    description:
      'Conexões entre APIs, bancos de dados, pagamentos, WhatsApp, CRMs e ferramentas externas.',
    points: [
      'APIs e webhooks',
      'Automações entre sistemas',
      'Sincronização de dados'
    ]
  },
  {
    number: '04',
    category: 'product',
    badge: 'Design',
    title: 'UI/UX Design',
    description:
      'Experiências digitais mais claras, bonitas e objetivas para produtos, sites e sistemas.',
    points: [
      'Design de interfaces',
      'Fluxos de navegação',
      'Prototipação e refinamento'
    ]
  },
  {
    number: '05',
    category: 'data',
    badge: 'Dados',
    title: 'Banco de Dados',
    description:
      'Modelagem, organização e estruturação de dados para sistemas mais seguros e eficientes.',
    points: [
      'Postgres e modelagem',
      'Consultas e performance',
      'Estrutura de dados escalável'
    ]
  },
  {
    number: '06',
    category: 'systems',
    badge: 'Backend',
    title: 'Back-end',
    description:
      'APIs, regras de negócio, autenticação e lógica central para produtos digitais robustos.',
    points: [
      'NestJS, Java e Python',
      'Autenticação e permissões',
      'Regras de negócio'
    ]
  },
  {
    number: '07',
    category: 'product',
    badge: 'Frontend',
    title: 'Front-end',
    description:
      'Interfaces modernas, interativas e responsivas conectadas ao backend e às regras do produto.',
    points: [
      'Vue, Nuxt e Tailwind',
      'Componentização',
      'Experiência fluida'
    ]
  },
  {
    number: '08',
    category: 'support',
    badge: 'Qualidade',
    title: 'Testes e Ajustes',
    description:
      'Validação de fluxos, correção de problemas e melhoria da estabilidade antes e depois do lançamento.',
    points: [
      'Testes de interface',
      'Correção de bugs',
      'Revisão de usabilidade'
    ]
  },
  {
    number: '09',
    category: 'support',
    badge: 'Evolução',
    title: 'Manutenção',
    description:
      'Suporte técnico, melhorias contínuas e evolução de sites, sistemas e produtos já existentes.',
    points: [
      'Atualizações contínuas',
      'Melhorias de performance',
      'Suporte pós-lançamento'
    ]
  }
]

const tracks = [
  {
    category: 'product',
    badge: 'MVP',
    timeline: '4–8 semanas',
    title: 'Produto do zero',
    description:
      'Para transformar uma ideia em um produto digital utilizável, validável e preparado para evolução.',
    deliverables: [
      'Discovery e escopo inicial',
      'Design da experiência',
      'Frontend e backend',
      'Deploy e handoff'
    ]
  },
  {
    category: 'systems',
    badge: 'Operação',
    timeline: '3–6 semanas',
    title: 'Backoffice sob medida',
    description:
      'Para empresas que precisam organizar processos internos, dados, usuários e rotinas operacionais.',
    deliverables: [
      'Painel administrativo',
      'Gestão de dados',
      'Permissões e usuários',
      'Relatórios essenciais'
    ]
  },
  {
    category: 'integrations',
    badge: 'Automação',
    timeline: '2–5 semanas',
    title: 'Integrações e automações',
    description:
      'Para conectar ferramentas, reduzir trabalho manual e criar fluxos automáticos entre sistemas.',
    deliverables: [
      'Mapeamento dos fluxos',
      'APIs e webhooks',
      'Rotinas automatizadas',
      'Monitoramento básico'
    ]
  }
]

const process = [
  {
    title: 'Diagnóstico',
    desc:
      'Entendemos o problema, o contexto, as restrições e o impacto esperado para o negócio.'
  },
  {
    title: 'Plano técnico',
    desc:
      'Definimos escopo, arquitetura, prioridades, riscos e a melhor forma de entregar valor rápido.'
  },
  {
    title: 'Construção',
    desc:
      'Desenvolvemos em ciclos curtos, com entregas visíveis, validações constantes e refinamento contínuo.'
  },
  {
    title: 'Lançamento',
    desc:
      'Publicamos, monitoramos e evoluímos o produto com base em uso real, dados e feedback.'
  }
]

const faqs = [
  {
    question: 'Quais tecnologias você utiliza?',
    answer:
      'A stack depende do projeto, mas normalmente trabalho com Vue, Nuxt, Tailwind, NestJS, Java, Python, Postgres, Prisma, Docker e integrações via APIs.'
  },
  {
    question: 'Quanto tempo leva para desenvolver?',
    answer:
      'Depende do escopo. Projetos menores podem levar algumas semanas, enquanto produtos mais completos exigem uma trilha maior de desenvolvimento e validação.'
  },
  {
    question: 'Você oferece suporte pós-lançamento?',
    answer:
      'Sim. O suporte pode incluir ajustes, melhorias, correções, manutenção, monitoramento e evolução contínua do produto.'
  },
  {
    question: 'Dá para começar com uma versão menor?',
    answer:
      'Sim. A ideia é começar pelo essencial, validar rápido e evoluir com base no uso real, evitando construir funcionalidades desnecessárias.'
  },
  {
    question: 'Você trabalha com código legado?',
    answer:
      'Sim. Nesse caso, o primeiro passo é avaliar a estrutura atual, identificar riscos e definir um plano seguro de melhoria.'
  }
]

function setCategory(category) {
  activeCategory.value = category
}

function clearFilters() {
  q.value = ''
  activeCategory.value = 'all'
}

function normalize(value) {
  return String(value || '').toLowerCase()
}

function matchesSearch(item) {
  const term = q.value.trim().toLowerCase()

  if (!term) return true

  const content = [
    item.title,
    item.description,
    item.badge,
    item.category,
    ...(item.points || []),
    ...(item.deliverables || [])
  ].join(' ')

  return normalize(content).includes(term)
}

function matchesCategory(item) {
  return activeCategory.value === 'all' || item.category === activeCategory.value
}

const filteredServices = computed(() =>
  services.filter(item => matchesCategory(item) && matchesSearch(item))
)

const filteredTracks = computed(() =>
  tracks.filter(item => matchesCategory(item) && matchesSearch(item))
)

function catalogSpan(index) {
  const layout = [
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-5 xl:-mt-2',
    'xl:col-span-7 xl:mt-8',
    'xl:col-span-6',
    'xl:col-span-6 xl:mt-8',
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-12'
  ]

  return layout[index % layout.length]
}

function serviceWhatsHref(service) {
  const text = [
    'Olá! Quero conversar sobre uma solução da Grivaro Studio.',
    '',
    `Serviço: ${service.title}`,
    `Categoria: ${service.badge}`,
    '',
    'Quero entender como isso pode ser aplicado à minha operação.'
  ].join('\n')

  return `https://wa.me/77999631644?text=${encodeURIComponent(text)}`
}

function trackWhatsHref(track) {
  const text = [
    'Olá! Quero conversar sobre uma trilha da Grivaro Studio.',
    '',
    `Trilha: ${track.title}`,
    `Prazo de referência: ${track.timeline}`,
    '',
    'Quero avaliar essa abordagem para o meu projeto.'
  ].join('\n')

  return `https://wa.me/77999631644?text=${encodeURIComponent(text)}`
}

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1

  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function setupReveal() {
  const elements = document.querySelectorAll('[data-reveal]')

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }

  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer?.unobserve(entry.target)
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -45px 0px'
    }
  )

  elements.forEach(element => observer.observe(element))
}

onMounted(() => {
  setupReveal()
  handleScroll()

  window.addEventListener('scroll', handleScroll, {
    passive: true
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, 0.90);
  --card: rgba(255, 255, 255, 0.76);
  --chip: rgba(245, 245, 247, 0.92);
  --glass: rgba(255, 255, 255, 0.54);
  --glass-strong: rgba(255, 255, 255, 0.84);

  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #ffffff;

  --border: rgba(24, 24, 27, 0.085);
  --focus: rgba(113, 113, 122, 0.18);

  --shadow-soft: 0 12px 38px rgba(24, 24, 27, 0.07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, 0.105);

  --hero-a: #0a0a0b;
  --hero-b: #7c7f87;

  --orb-one: rgba(147, 161, 185, 0.22);
  --orb-two: rgba(203, 213, 225, 0.31);
  --orb-three: rgba(226, 232, 240, 0.28);

  --cta: rgba(255, 255, 255, 0.92);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, 0.90);
  --card: rgba(19, 20, 24, 0.76);
  --chip: rgba(26, 27, 31, 0.92);
  --glass: rgba(19, 20, 23, 0.54);
  --glass-strong: rgba(18, 19, 22, 0.84);

  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;

  --border: rgba(255, 255, 255, 0.085);
  --focus: rgba(212, 212, 216, 0.13);

  --shadow-soft: 0 12px 42px rgba(0, 0, 0, 0.30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, 0.50);

  --hero-a: #ffffff;
  --hero-b: #777a84;

  --orb-one: rgba(71, 85, 105, 0.23);
  --orb-two: rgba(63, 63, 70, 0.28);
  --orb-three: rgba(39, 39, 42, 0.28);

  --cta: rgba(18, 19, 22, 0.94);
}

:global(html) {
  background: var(--page);
  transition: background-color 350ms ease;
}

.site-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

.ambient-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, var(--border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 80%);
}

.ambient-noise {
  opacity: 0.021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(42rem circle at var(--pointer-x) var(--pointer-y),
      var(--orb-one),
      transparent 68%);
  transition: background 120ms linear;
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px);
  will-change: transform;
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 45rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite;
}

.ambient-orb-three {
  left: 30%;
  top: 120rem;
  width: 30rem;
  height: 30rem;
  background: var(--orb-three);
  animation: float-c 21s ease-in-out infinite;
}

.hero-gradient {
  background: linear-gradient(120deg, var(--hero-a) 5%, var(--hero-b) 96%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    transform 300ms ease,
    box-shadow 300ms ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 15px 32px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition:
    transform 300ms ease,
    border-color 300ms ease;
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent);
}

.hero-halo,
.track-halo,
.cta-orb {
  background: var(--orb-one);
}

.device-frame {
  background:
    linear-gradient(135deg,
      color-mix(in srgb, var(--text-strong) 16%, transparent),
      color-mix(in srgb, var(--text-strong) 4%, transparent));
  border: 1px solid var(--border);
  box-shadow:
    var(--shadow-large),
    inset 0 1px 0 rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(18px);
}

.dashboard-card {
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.075);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
  transition:
    background-color 300ms ease,
    transform 300ms ease;
}

.dashboard-card:hover {
  background: rgba(255, 255, 255, 0.065);
  transform: translateY(-2px);
}

.search-dock {
  transition:
    transform 400ms ease,
    box-shadow 400ms ease;
}

.search-dock:focus-within {
  transform: translateY(-2px);
  box-shadow: var(--shadow-large);
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 25%,
      var(--border) 75%,
      transparent);
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow:
    0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%);
}

.eyebrow {
  font-size: 0.72rem;
  line-height: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--muted);
}

.section-title {
  max-width: 52rem;
  font-size: clamp(2.35rem, 4.7vw, 4rem);
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -0.055em;
  color: var(--text-strong);
}

.service-card,
.track-card {
  backdrop-filter: blur(18px);
}

.service-card:hover,
.track-card:hover {
  transform: translateY(-6px);
  border-color: color-mix(in srgb, var(--text-strong) 14%, transparent);
  box-shadow: var(--shadow-soft);
}

.card-shine {
  position: absolute;
  inset: -1px;
  opacity: 0;
  pointer-events: none;
  background:
    radial-gradient(24rem circle at 20% 0%,
      color-mix(in srgb, var(--text-strong) 7%, transparent),
      transparent 60%);
  transition: opacity 450ms ease;
}

.service-card:hover .card-shine {
  opacity: 1;
}

.curve-transition {
  position: relative;
  width: 100%;
  height: 5.5rem;
  overflow: hidden;
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--border);
  border-radius: 50%;
  transform: translateY(18%);
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2.25rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--text-strong);
  transform: translateX(-50%);
}

.process-shell {
  backdrop-filter: blur(24px);
}

.process-glow {
  position: absolute;
  right: -8rem;
  top: -8rem;
  width: 25rem;
  height: 25rem;
  border-radius: 9999px;
  background: var(--orb-one);
  filter: blur(80px);
  opacity: 0.75;
}

.timeline-line {
  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 8%,
      var(--border) 90%,
      transparent);
}

.dark-panel {
  background:
    radial-gradient(circle at 85% 20%, rgba(255, 255, 255, .07), transparent 30%),
    #090a0c;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .08),
    0 36px 110px rgba(0, 0, 0, .22);
}

.dark-panel-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, .055) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, .055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%);
}

.dark-orb {
  background: rgba(255, 255, 255, .07);
}

.faq-item[open] {
  padding-bottom: 1.65rem;
}

.cta-panel {
  background:
    linear-gradient(135deg,
      var(--cta),
      color-mix(in srgb, var(--cta) 85%, var(--page)));
}

[data-reveal] {
  opacity: 0;
  transform: translateY(26px) scale(.992);
  transition:
    opacity 780ms cubic-bezier(.22, .82, .22, 1),
    transform 780ms cubic-bezier(.22, .82, .22, 1);
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.catalog-enter-active,
.catalog-leave-active,
.catalog-move {
  transition:
    opacity 350ms ease,
    transform 350ms ease;
}

.catalog-enter-from,
.catalog-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.98);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}

@keyframes float-a {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(3rem, 2rem, 0);
  }
}

@keyframes float-b {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(-3rem, -2rem, 0);
  }
}

@keyframes float-c {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(1rem, -3rem, 0);
  }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size: 44px 44px;
  }

  .curve-transition::before {
    left: 2%;
    width: 96%;
  }
}

@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  [data-reveal] {
    opacity: 1;
    transform: none;
  }
}
</style>
